import { DowntimesStatus, RequestStatusType } from 'shared/enums';
import { Pagination } from 'shared/types/types';
import { edpApiV1 } from '..';
import { TSingleDayOffGetRequest } from './dayoffSupervisor';

export type TVacationScheduleRequestSupervisor = {
  id: number;
  type: DowntimesStatus;
  status: {
    name: string;
    type: RequestStatusType;
  };
  comment: string | null;
  endedAt: string;
  isUnpaid: boolean;
  startedAt: string;
  amountDays: number;
  vacationType: {
    id: string;
    name: string;
  };
  rejectionReason: string | null;
};

export type VacationScheduleSupervisor = {
  id: number;
  name: string;
  lastName: string;
  middleName: string;
  image: any;
  division: {
    id: string;
    name: string;
    code: string;
  };
  position: {
    id: string;
    name: string;
  };
  requests?: TVacationScheduleRequestSupervisor[];
  scheduleRequests?: TVacationScheduleRequestSupervisor[];
  step: {
    depth: number;
    divisionId: string;
    divisionName: string;
    divisionCode: string;
  };
  amountSteps: number;
};

export type TVacationScheduleSupervisorGetRequest = {
  search?: string;
  page: number;
  size: number;
  divisionId?: string;
};

export interface TVacationScheduleRejectGetRequest extends TSingleDayOffGetRequest {
  comment: string;
}

export type VacationScheduleSupervisorPaginated = {
  data: VacationScheduleSupervisor[];
  pagination: Pagination;
};

export interface TVacationScheduleSupervisorApprove {
  divisionId?: string;
  employeeIds: number[];
}

export const vacationsScheduleSupervisorApi = edpApiV1.injectEndpoints({
  endpoints: build => ({
    vacationsScheduleDeputy: build.query<VacationScheduleSupervisorPaginated, TVacationScheduleSupervisorGetRequest>({
      query: data => {
        const { divisionId, ...params } = data;
        return {
          url: `/v1/dublicate/deputy/vacation-schedule/subordinates/${divisionId}`,
          method: 'GET',
          params,
        };
      },
      providesTags: ['vacationScheduleDeputy'],
    }),
    singleVacationsScheduleDeputy: build.query<VacationScheduleSupervisor, TSingleDayOffGetRequest>({
      query: ({ requestId, employeeId, divisionId }) => ({
        url: `/v1/dublicate/deputy/vacation-schedule/subordinates/${divisionId}/${employeeId}/requests/${requestId}`,
        method: 'GET',
      }),
      providesTags: ['vacationScheduleDeputy'],
    }),
    vacationScheduleRejectDeputy: build.mutation<string, TVacationScheduleRejectGetRequest>({
      query: ({ employeeId, requestId, divisionId, ...body }) => ({
        url: `/v1/dublicate/deputy/vacation-schedule/subordinates/${divisionId}/${employeeId}/requests/${requestId}/reject`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['vacationScheduleDeputy'],
    }),
    vacationScheduleDownloadFileDeputy: build.mutation<string, TVacationScheduleRejectGetRequest>({
      query: ({ employeeId, requestId, divisionId, ...body }) => ({
        url: `/v1/dublicate/deputy/vacation-schedule/subordinates/${divisionId}/download-file`,
        method: 'GET',
        body,
      }),
      invalidatesTags: ['vacationScheduleDeputy'],
    }),
    vacationScheduleApproveDeputy: build.mutation<string, TVacationScheduleSupervisorApprove>({
      query: ({ divisionId, ...body }) => ({
        url: `/v1/dublicate/deputy/vacation-schedule/subordinates/${divisionId}/requests/approve`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['vacationScheduleDeputy'],
    }),
    vacationScheduleSendToHrDeputy: build.mutation<string, { divisionId: string }>({
      query: ({ divisionId, ...body }) => ({
        url: `/v1/dublicate/deputy/vacation-schedule/subordinates/${divisionId}/requests/send-to-hr`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['vacationScheduleDeputy'],
    }),
    vacationsScheduleSupervisor: build.query<
      VacationScheduleSupervisorPaginated,
      TVacationScheduleSupervisorGetRequest
    >({
      query: params => ({
        url: '/v1/dublicate/supervisor/vacation-schedule/subordinates',
        method: 'GET',
        params,
      }),
      providesTags: ['vacationScheduleSupervisor'],
    }),
    singleVacationsScheduleSupervisor: build.query<VacationScheduleSupervisor, TSingleDayOffGetRequest>({
      query: ({ requestId, employeeId }) => ({
        url: `/v1/dublicate/supervisor/vacation-schedule/subordinates/${employeeId}/requests/${requestId}`,
        method: 'GET',
      }),
      providesTags: ['vacationScheduleSupervisor'],
    }),
    vacationScheduleRejectSupervisor: build.mutation<string, TVacationScheduleRejectGetRequest>({
      query: ({ employeeId, requestId, ...body }) => ({
        url: `/v1/dublicate/supervisor/vacation-schedule/subordinates/${employeeId}/requests/${requestId}/reject`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['vacationScheduleSupervisor'],
    }),
    vacationScheduleDownloadFileSupervisor: build.mutation<string, TVacationScheduleRejectGetRequest>({
      query: ({ employeeId, requestId, ...body }) => ({
        url: `/v1/dublicate/supervisor/vacation-schedule/subordinates/download-file`,
        method: 'GET',
        body,
      }),
      invalidatesTags: ['vacationScheduleSupervisor'],
    }),
    vacationScheduleApproveSupervisor: build.mutation<string, TVacationScheduleSupervisorApprove>({
      query: body => ({
        url: `/v2/dublicate/supervisor/vacation-schedule/subordinates/requests/approve`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['vacationScheduleSupervisor'],
    }),
    vacationScheduleSendToHrSupervisor: build.mutation<string, void>({
      query: body => ({
        url: `/v1/dublicate/supervisor/vacation-schedule/subordinates/requests/send-to-hr`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['vacationScheduleSupervisor'],
    }),
    vacationsScheduleActing: build.query<VacationScheduleSupervisorPaginated, TVacationScheduleSupervisorGetRequest>({
      query: ({ divisionId, ...params }) => ({
        url: `/v1/dublicate/acting/${divisionId}/vacation-schedule/subordinates`,
        method: 'GET',
        params,
      }),
      providesTags: ['vacationScheduleActing'],
    }),
    singleVacationsScheduleActing: build.query<VacationScheduleSupervisor, TSingleDayOffGetRequest>({
      query: ({ requestId, employeeId, divisionId }) => ({
        url: `/v1/dublicate/acting/${divisionId}/vacation-schedule/subordinates/${employeeId}/requests/${requestId}`,
        method: 'GET',
      }),
      providesTags: ['vacationScheduleActing'],
    }),
    vacationScheduleRejectActing: build.mutation<string, TVacationScheduleRejectGetRequest>({
      query: ({ divisionId, employeeId, requestId, ...body }) => ({
        url: `/v1/dublicate/acting/${divisionId}/vacation-schedule/subordinates/${employeeId}/requests/${requestId}/reject`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['vacationScheduleActing'],
    }),
    vacationScheduleDownloadFileActing: build.mutation<string, TVacationScheduleRejectGetRequest>({
      query: ({ divisionId, employeeId, requestId, ...body }) => ({
        url: `/v1/dublicate/acting/${divisionId}/vacation-schedule/subordinates/download-file`,
        method: 'GET',
        body,
      }),
      invalidatesTags: ['vacationScheduleActing'],
    }),
    vacationScheduleApproveActing: build.mutation<string, TVacationScheduleSupervisorApprove>({
      query: ({ divisionId, ...body }) => ({
        url: `/v1/dublicate/acting/${divisionId}/vacation-schedule/subordinates/requests/approve`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['vacationScheduleActing'],
    }),
    vacationScheduleSendToHrActing: build.mutation<string, { divisionId: string }>({
      query: ({ divisionId }) => ({
        url: `/v1/dublicate/acting/${divisionId}/vacation-schedule/subordinates/requests/send-to-hr`,
        method: 'PATCH',
      }),
      invalidatesTags: ['vacationScheduleActing'],
    }),
  }),
  overrideExisting: false,
});
